import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { queryAllDocsByFieldSnap } from "../../../utils/firebase/Firebase.utils";
import UserScreenWindow from "../Components/UserScreenWindow";
import { useNavigate } from "react-router-dom";
import {
  FiPackage,
  FiHome,
  FiUser,
  FiMail,
  FiClock,
  FiGrid,
  FiList,
  FiBox,
} from "react-icons/fi";

export default function ParcelForUser() {
  const currentUser = useSelector((state) => state.userObject);
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const filters = ["All", "Stored", "Claimed"];

  useEffect(() => {
    const unsubscribes = [];
    setLoading(true);

    const fetchParcels = async () => {
      for (const identifier of currentUser.linkedIdentifiers) {
        const unsubscribe = queryAllDocsByFieldSnap(
          "parcels",
          "unitNumber",
          "==",
          identifier,
          (data) => {
            setParcels((prevParcels) => {
              // Remove any existing parcels for this identifier
              const filteredParcels = prevParcels.filter(
                (parcel) => parcel.unitNumber !== identifier
              );
              // Add the new parcels for this identifier
              return [...filteredParcels, ...data];
            });
            setLoading(false);
          }
        );
        unsubscribes.push(unsubscribe);
      }
    };

    if (currentUser.linkedIdentifiers?.length) {
      fetchParcels();
    }

    // Cleanup function to unsubscribe from all snapshots
    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [currentUser.linkedIdentifiers]);

  const handleParcelClick = (parcelId) => {
    navigate(`/parcel/${parcelId}`);
  };

  // Filter parcels based on selected status
  const filteredParcels = parcels.filter((parcel) => {
    if (statusFilter === "all") return true;
    return parcel.status.toLowerCase() === statusFilter.toLocaleLowerCase();
  });

  return (
    <UserScreenWindow label="Your Parcels">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Modern Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FiBox className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Parcel Management
              </h1>
              <p className="text-xs text-gray-500">
                Track and manage your deliveries
              </p>
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="flex flex-col space-y-3 mb-6">
          {/* View Mode Toggle */}
          <div className="flex items-center justify-between">
            <div className="inline-flex rounded-lg shadow-sm bg-gray-50">
              {filters.map((filter, index) => (
                <button
                  key={index}
                  onClick={() => setStatusFilter(filter.toLocaleLowerCase())}
                  className={`px-4 py-2 text-xs font-medium flex items-center ${
                    index === 0
                      ? "rounded-l-lg"
                      : index === filters.length - 1
                      ? "rounded-r-lg"
                      : ""
                  } ${
                    statusFilter === filter.toLocaleLowerCase()
                      ? "bg-[#363062] text-white shadow-sm"
                      : "bg-transparent text-[#363062] hover:bg-[#363062]/5"
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>

            <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden shadow-sm">
              <button
                onClick={() => setViewMode("cards")}
                className={`p-2 text-xs font-medium flex items-center ${
                  viewMode === "cards"
                    ? "bg-[#363062] text-white"
                    : "text-[#363062] hover:bg-[#363062]/5"
                }`}
              >
                <FiGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 text-xs font-medium flex items-center ${
                  viewMode === "list"
                    ? "bg-[#363062] text-white"
                    : "text-[#363062] hover:bg-[#363062]/5"
                }`}
              >
                <FiList className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#8FCD00]"></div>
          </div>
        ) : error ? (
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg"
            role="alert"
          >
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        ) : filteredParcels.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
            <div className="p-3 bg-[#8FCD00]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <FiPackage className="text-[#8FCD00] text-lg" />
            </div>
            <h3 className="text-sm font-medium text-[#363062] mb-1">
              No Parcels Found
            </h3>
            <p className="text-xs text-gray-500">
              There are no parcels for your household at the moment
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "cards" ? "grid grid-cols-2 gap-4" : "space-y-3"
            }
          >
            {filteredParcels.map((parcel) => (
              <div
                key={parcel.id}
                className={`bg-white rounded-lg ${
                  viewMode === "cards"
                    ? "shadow-sm hover:shadow-md"
                    : "shadow-sm"
                } border border-gray-100 overflow-hidden transition-all hover:border-[#8FCD00]/30 cursor-pointer`}
                onClick={() => handleParcelClick(parcel.id)}
              >
                {viewMode === "cards" && (
                  <div className="h-24 bg-gray-50 flex items-center justify-center border-b border-gray-100">
                    {parcel.imageUrl ? (
                      <img
                        src={parcel.imageUrl}
                        alt={`Parcel ${parcel.parcelNumber}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FiPackage className="h-8 w-8 text-[#363062]/40" />
                    )}
                  </div>
                )}
                <div className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <h2 className="text-sm font-medium text-[#363062]">
                      #{parcel.parcelNumber}
                    </h2>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        parcel.status === "Stored"
                          ? "bg-[#8FCD00]/10 text-[#8FCD00]"
                          : parcel.status === "Delivered"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {parcel.status}
                    </span>
                  </div>

                  <div
                    className={`grid ${
                      viewMode === "cards" ? "grid-cols-1" : "grid-cols-2"
                    } gap-2 text-xs`}
                  >
                    <div className="flex items-center">
                      <FiHome className="h-3.5 w-3.5 mr-1.5 text-[#8FCD00]" />
                      <span className="text-gray-600">{parcel.unitNumber}</span>
                    </div>

                    <div className="flex items-center">
                      <FiUser className="h-3.5 w-3.5 mr-1.5 text-[#8FCD00]" />
                      <span className="text-gray-600 truncate">
                        {parcel.recipientName}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <FiMail className="h-3.5 w-3.5 mr-1.5 text-[#8FCD00]" />
                      <span className="text-gray-600 truncate">
                        {parcel.deliveryCompany}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <FiClock className="h-3.5 w-3.5 mr-1.5 text-[#8FCD00]" />
                      <span className="text-gray-600 text-[11px]">
                        {new Date(parcel.createdAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </UserScreenWindow>
  );
}